import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Check,
  Info,
  Sparkles,
  Calculator,
  TrendingUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency, getAdventureEmoji } from '@/utils/adventureUtils';

interface PriceBreakdownItem {
  id: string;
  label: string;
  description: string;
  amount: number;
  type: 'base' | 'option';
}

interface MobileActionBarProps {
  adventures: CustomAdventure[];
  selectedAdventure: CustomAdventure | null;
  currentPrice: { amount: number; currency: string } | null;
  priceBreakdown?: PriceBreakdownItem[];
  onAdventureSelect: (adventure: CustomAdventure) => void;
  onResetSelection: () => void;
}

export const MobileActionBar = ({
  adventures,
  selectedAdventure,
  currentPrice,
  priceBreakdown = [],
  onAdventureSelect,
  onResetSelection
}: MobileActionBarProps) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  if (!adventures || adventures.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      {/* Adventure Switcher (when adventure is selected) */}
      {selectedAdventure && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          className="bg-white border-t border-gray-200 shadow-lg max-h-48 overflow-hidden"
        >
          <div className="container mx-auto px-4 py-3">
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-600">Switch Adventure</h4>
            </div>
            
            {/* Horizontal scrollable adventure list */}
            <div 
              className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4"
              style={{
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {adventures.map((adventure) => (
                <button
                  key={adventure.id}
                  onClick={() => onAdventureSelect(adventure)}
                  className={cn(
                    "flex-shrink-0 flex items-center gap-2 p-2 rounded-lg border transition-all duration-200",
                    selectedAdventure?.id === adventure.id
                      ? "bg-primary border-primary text-white"
                      : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                  )}
                >
                  <span className="text-lg">{getAdventureEmoji(adventure)}</span>
                  <div className="text-left min-w-0">
                    <div className="text-xs font-medium truncate max-w-20">
                      {adventure.name}
                    </div>
                    <div className="text-xs opacity-75">
                      {formatCurrency(adventure?.basePrice || 0, adventure?.currency || 'EUR')}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Main Action Bar */}
      <div className="bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-xl">
        <div className="container mx-auto px-4 py-3">
          {selectedAdventure ? (
            // Selected adventure display with pricing
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-[#1a3d1d] rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-lg">{selectedAdventure ? getAdventureEmoji(selectedAdventure) : '🏔️'}</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {selectedAdventure.name}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {currentPrice ? 'Total:' : 'From:'}
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {currentPrice 
                        ? formatCurrency(currentPrice.amount, currentPrice.currency)
                        : formatCurrency(selectedAdventure?.basePrice || 0, selectedAdventure?.currency || 'EUR')
                      }
                    </span>
                    {currentPrice && (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 border-0 px-1.5 py-0.5">
                        Live Price
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Show price details button - always visible when adventure is selected */}
                {selectedAdventure && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300 px-2 py-1 h-auto"
                      >
                        <Calculator className="w-3 h-3" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Calculator className="w-4 h-4" />
                          Price Breakdown
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Adventure Info */}
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl">{getAdventureEmoji(selectedAdventure)}</div>
                          <div>
                            <div className="font-medium text-gray-900">{selectedAdventure.name}</div>
                            <div className="text-sm text-gray-500">Custom Adventure</div>
                          </div>
                        </div>

                        {/* Price Summary */}
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                            <div>
                              <div className="text-sm text-gray-600">Base Price</div>
                              <div className="text-xs text-gray-500">Starting rate</div>
                            </div>
                            <div className="text-lg font-semibold text-primary">
                              {formatCurrency(selectedAdventure?.basePrice || 0, selectedAdventure?.currency || 'EUR')}
                            </div>
                          </div>

                          {currentPrice ? (
                            <>
                              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                <div>
                                  <div className="text-sm text-gray-600">Live Total</div>
                                  <div className="text-xs text-gray-500">With your selections</div>
                                </div>
                                <div className="text-xl font-bold text-blue-600">
                                  {formatCurrency(currentPrice.amount, currentPrice.currency)}
                                </div>
                              </div>

                              {/* Savings/Addition indicator */}
                              {currentPrice.amount !== (selectedAdventure?.basePrice || 0) && (
                                <div className="flex items-center justify-center gap-2 text-sm">
                                  <TrendingUp className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-600">
                                    {currentPrice.amount > (selectedAdventure?.basePrice || 0) 
                                      ? `+${formatCurrency(currentPrice.amount - (selectedAdventure?.basePrice || 0), currentPrice.currency)} with options`
                                      : 'Base price applies'
                                    }
                                  </span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <div>
                                <div className="text-sm text-gray-600">Current Price</div>
                                <div className="text-xs text-gray-500">Customize to see live pricing</div>
                              </div>
                              <div className="text-xl font-bold text-gray-600">
                                {formatCurrency(selectedAdventure?.basePrice || 0, selectedAdventure?.currency || 'EUR')}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Detailed Price Breakdown */}
                        {priceBreakdown.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700 border-b pb-1">
                              Price Breakdown
                            </div>
                            {priceBreakdown.map((item) => (
                              <div key={item.id} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-700 truncate">
                                    {item.label}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {item.description}
                                  </div>
                                </div>
                                <div className="ml-3 text-right">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(item.amount, selectedAdventure?.currency || 'EUR')}
                                  </div>
                                  <div className="text-xs text-gray-500 capitalize">
                                    {item.type === 'base' ? 'Base' : 'Add-on'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Note */}
                        <div className="text-xs text-gray-500 text-center p-2 bg-amber-50 rounded border border-amber-200">
                          💡 This is a live estimate. Final pricing will be confirmed during booking.
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                <Button
                  onClick={onResetSelection}
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400 px-3 py-2 h-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  <span className="hidden xs:inline">{t('header.backToAdventures')}</span>
                  <span className="xs:hidden">Back</span>
                </Button>
              </div>
            </div>
          ) : (
            // No adventure selected - show selection prompt
            <div className="text-center py-2">
              <p className="text-sm text-gray-600 mb-2">Choose an adventure to customize</p>
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-primary">
                  {adventures.length} custom adventures available
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
